import { ArrowLeft, Check, Star, Crown, Infinity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import AdPanel from '@/components/ads/AdPanel';
import LegalFooter from '@/components/common/LegalFooter';
const Pricing = () => {
  const navigate = useNavigate();
  const {
    t,
    currentLanguage
  } = useLanguage();
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  const formatPrice = (usd: number) => {
    if (currentLanguage === 'de') {
      return `€${(usd * 0.93).toFixed(2)}`;
    }
    return `$${usd.toFixed(2)}`;
  };
  const plans = [{
    name: "Alpha200",
    subtitle: "Free Plan",
    price: 0,
    period: "monthly",
    icon: Star,
    color: "text-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800",
    borderColor: "border-gray-200 dark:border-gray-700",
    features: ["View up to 200 profiles per day", "Basic matching algorithm", "Limited messages (5 per day)", "Basic profile features", "Standard support"],
    limitations: ["No unlimited swipes", "Cannot see who liked you", "Limited photo uploads (3 max)", "No priority support"]
  }, {
    name: "Alpha600",
    subtitle: "Premium Plan",
    price: 14.99,
    period: "monthly",
    icon: Crown,
    color: "text-pink-500",
    bgColor: "bg-pink-50 dark:bg-pink-900/20",
    borderColor: "border-pink-200 dark:border-pink-500",
    badge: "Most Popular",
    features: ["View up to 600 profiles per day", "Advanced matching algorithm", "Unlimited messaging", "See who liked your profile", "Advanced search filters", "Read receipts", "Upload up to 12 photos", "Priority customer support", "Hide ads", "Boost your profile 2x per month"]
  }, {
    name: "Alpha∞",
    subtitle: "Unlimited Plan",
    price: 29.99,
    period: "monthly",
    icon: Infinity,
    color: "text-purple-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-500",
    badge: "Premium",
    features: ["Unlimited profile views", "AI-powered matching", "Unlimited messaging", "See who liked your profile", "Advanced search & filters", "Read receipts & typing indicators", "Unlimited photo uploads", "24/7 VIP support", "Completely ad-free experience", "Unlimited profile boosts", "Super likes (5 per day)", "Travel mode", "Incognito browsing", "Profile verification badge"]
  }];
  return <div className="min-h-screen bg-carbon">
      <AdPanel />
      
      <div className="p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center mb-6">
            <Button onClick={handleBack} variant="ghost" size="sm" className="mr-3 text-ice-white hover:bg-ice-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center">
              <img alt="ALPHA Logo" className="w-8 h-8 mr-3" src="/lovable-uploads/89c148ec-e770-4e43-aea9-994a4948256c.png" />
              <h1 className="text-2xl font-bold text-ice-white">Transparent Pricing</h1>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-ice-white mb-4">Choose Your Perfect Plan</h2>
            <p className="text-ice-white/80 text-lg max-w-2xl mx-auto">
              Find the plan that works best for you. All prices include applicable taxes. 
              Cancel anytime with our 14-day money-back guarantee.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {plans.map(plan => {
            const Icon = plan.icon;
            return <Card key={plan.name} className={`relative ${plan.bgColor} ${plan.borderColor} border-2 transition-all hover:scale-105`}>
                  {plan.badge && <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-neon-red text-ice-white">
                      {plan.badge}
                    </Badge>}
                  
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 rounded-full ${plan.color === 'text-gray-500' ? 'bg-gray-200' : 'bg-ice-white'} flex items-center justify-center mx-auto mb-4`}>
                      <Icon className={`w-8 h-8 ${plan.color}`} />
                    </div>
                    <CardTitle className="text-2xl font-bold text-ice-white">
                      {plan.name}
                    </CardTitle>
                    <p className="text-ice-white/60">{plan.subtitle}</p>
                    <div className="text-center mt-4">
                      <div className="text-4xl font-bold text-ice-white">
                        {plan.price === 0 ? 'Free' : formatPrice(plan.price)}
                      </div>
                      {plan.price > 0 && <div className="text-ice-white/60">
                          per {plan.period}
                          {currentLanguage === 'de' && <div className="text-xs mt-1">
                              Inkl. 19% MwSt.
                            </div>}
                        </div>}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => <div key={index} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-ice-white/80 text-sm">{feature}</span>
                        </div>)}
                      {plan.limitations && <div className="pt-3 border-t border-ice-white/10">
                          <p className="text-ice-white/60 text-xs mb-2">Limitations:</p>
                          {plan.limitations.map((limitation, index) => <div key={index} className="flex items-start gap-3">
                              <div className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400">×</div>
                              <span className="text-ice-white/60 text-xs">{limitation}</span>
                            </div>)}
                        </div>}
                    </div>
                    
                    <Button className={`w-full ${plan.price === 0 ? 'bg-gray-600 hover:bg-gray-700' : 'bg-neon-red hover:bg-neon-red/80'}`} disabled={plan.price === 0}>
                      {plan.price === 0 ? 'Current Plan' : 'Choose Plan'}
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>

          <Card className="bg-carbon border-ice-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-ice-white">Billing Information</CardTitle>
            </CardHeader>
            <CardContent className="text-ice-white/80 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">Payment Methods</h4>
                  <ul className="space-y-1 text-sm">
                    <li>• Credit Card (Visa, Mastercard, American Express)</li>
                    <li>• PayPal</li>
                    <li>• Apple Pay / Google Pay</li>
                    <li>• SEPA Direct Debit (EU only)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">Billing Cycle</h4>
                  <ul className="space-y-1 text-sm">
                    <li>• Monthly billing on the same date each month</li>
                    <li>• Automatic renewal (can be cancelled anytime)</li>
                    <li>• Prorated refunds for cancellations</li>
                    <li>• No hidden fees or setup costs</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-carbon border-ice-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-ice-white">Cancellation & Refund Policy</CardTitle>
            </CardHeader>
            <CardContent className="text-ice-white/80 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">14-Day Money-Back Guarantee</h4>
                  <p className="text-sm">
                    You have 14 days from your subscription start date to cancel and receive a full refund. 
                    This applies to your first payment only.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">Easy Cancellation</h4>
                  <p className="text-sm">
                    Cancel anytime through your account settings or by contacting support. 
                    No cancellation fees. Your subscription remains active until the end of your billing period.
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-ice-white/10">
                <Button variant="outline" className="mr-4" onClick={() => navigate('/cancellation-rights')}>
                  View Cancellation Rights
                </Button>
                <Button variant="outline" onClick={() => navigate('/terms-of-service')}>
                  Terms of Service
                </Button>
              </div>
            </CardContent>
          </Card>

          <LegalFooter />
        </div>
      </div>
    </div>;
};
export default Pricing;